/**
 * Utility functions for handling API errors (Issue #964)
 */

import axios, { AxiosError } from 'axios';
import { ApiError } from '../types';

/**
 * Default error message when no specific error information is available
 */
export const DEFAULT_ERROR_MESSAGE = 'Unknown error occurred';

interface ApiErrorResponse {
  response?: {
    data?: {
      detail?: string;
      message?: string;
      code?: string;
    };
    status?: number;
    statusText?: string;
  };
  message?: string;
}

/**
 * Extract a user-friendly error message from an API error
 * @param error - The error object from the API call
 * @param fallbackMessage - Default message if no specific error is found
 * @returns User-friendly error message
 */
export const getErrorMessage = (error: unknown, fallbackMessage: string = 'An error occurred'): string => {
  if (!error) return fallbackMessage;

  const apiError = error as ApiErrorResponse;

  // Try to extract error detail from response
  if (apiError.response?.data?.detail) {
    return apiError.response.data.detail;
  }

  // Try alternative message field
  if (apiError.response?.data?.message) {
    return apiError.response.data.message;
  }

  // Try HTTP status-based messages
  if (apiError.response?.status) {
    switch (apiError.response.status) {
      case 400:
        return 'Invalid request. Please check your input.';
      case 401:
        return 'Authentication required. Please log in again.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 409:
        return 'A conflict occurred. The resource may already exist.';
      case 422:
        return 'Validation error. Please check your input.';
      case 429:
        return 'Too many requests. Please try again later.';
      case 500:
        return 'Server error. Please try again later.';
      case 503:
        return 'Service temporarily unavailable. Please try again later.';
      default:
        return `Request failed with status ${apiError.response.status}: ${apiError.response.statusText || 'Unknown error'}`;
    }
  }

  // Try error message
  if (apiError.message) {
    return apiError.message;
  }

  // Fallback
  return fallbackMessage;
};

/**
 * Extract validation errors from API response
 * @param error - The error object from the API call
 * @returns Array of validation error messages
 */
interface ValidationError {
  loc?: string[];
  msg?: string;
}

export const getValidationErrors = (error: unknown): string[] => {
  if (!error) return [];

  const apiError = error as ApiErrorResponse;

  // Check for validation errors in response
  if (apiError.response?.data && typeof apiError.response.data === 'object') {
    const data = apiError.response.data as unknown as { detail?: ValidationError[] };

    // FastAPI validation errors
    if (Array.isArray(data.detail)) {
      return data.detail.map((err: ValidationError) => {
        const field = err.loc?.join('.') || 'Unknown field';
        const message = err.msg || 'Invalid value';
        return `${field}: ${message}`;
      });
    }
  }

  return [];
};

/**
 * Type guard to check if error is an Axios error
 * @param error - The error to check
 * @returns True if error is an AxiosError
 */
export function isAxiosError(error: unknown): error is AxiosError {
  return axios.isAxiosError(error);
}

/**
 * Get user-friendly error message based on HTTP status code
 * @param status - HTTP status code
 * @returns User-friendly error message
 */
function getStatusMessage(status: number): string {
  switch (status) {
    case 400:
      return 'Invalid request. Please check your input.';
    case 401:
      return 'Authentication required. Please log in again.';
    case 403:
      return 'You do not have permission to perform this action.';
    case 404:
      return 'The requested resource was not found.';
    case 409:
      return 'A conflict occurred. The resource may already exist.';
    case 422:
      return 'Validation error. Please check your input.';
    case 429:
      return 'Too many requests. Please try again later.';
    case 500:
      return 'Server error. Please try again later.';
    case 503:
      return 'Service temporarily unavailable. Please try again later.';
    default:
      return `Request failed with status ${status}`;
  }
}

/**
 * Extract structured API error from unknown error
 * @param error - The error to extract from
 * @returns Structured ApiError object with user-friendly messages
 */
export function extractApiError(error: unknown): ApiError {
  if (isAxiosError(error)) {
    const responseData = error.response?.data as { detail?: string; message?: string; code?: string } | undefined;
    const status = error.response?.status;

    // Use API-provided message, or fall back to status-based friendly message
    let message = responseData?.detail || responseData?.message;
    if (!message && status) {
      message = getStatusMessage(status);
    }
    if (!message) {
      message = error.message;
    }

    return {
      message,
      status,
      code: responseData?.code,
      details: error.response?.data as Record<string, unknown>,
    };
  }

  if (error instanceof Error) {
    return {
      message: error.message,
    };
  }

  return {
    message: DEFAULT_ERROR_MESSAGE,
  };
}
